import { useQuery } from "@apollo/client";
import { useLocation } from "react-router-dom";
import Dialog from "@/components/Dialog";
import Button from "@/components/Button";
import { useNavigate } from "@/router";
import { useCanViewPersonSuspensionHistoryBool } from "@/hooks/useCapabilities";
import { buildActiveYearsBreakdown, formatYearsAndMonths } from "./activeYearsBreakdown";
import { ACTIVE_YEARS_BREAKDOWN_QUERY, ACTIVE_YEARS_BREAKDOWN_SUSPENSIONS_QUERY } from "./activeYearsBreakdownQueries";
import ActiveYearsSummaryPanel from "./ActiveYearsSummaryPanel";
import ActiveYearsBandsTable from "./ActiveYearsBandsTable";

type Props = {
  open: boolean;
  onClose: () => void;
  pgaId: string;
  activeYearsValue: string | null;
};

export default function ActiveYearsBreakdownModal({ open, onClose, pgaId, activeYearsValue }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const canViewPersonSuspensionHistory = useCanViewPersonSuspensionHistoryBool();
  const { data, loading } = useQuery(ACTIVE_YEARS_BREAKDOWN_QUERY, {
    variables: { pgaId },
    skip: !open,
    fetchPolicy: "cache-first"
  });
  const { data: suspensionsData, loading: suspensionsLoading } = useQuery(ACTIVE_YEARS_BREAKDOWN_SUSPENSIONS_QUERY, {
    variables: { pgaId },
    skip: !open || !canViewPersonSuspensionHistory,
    fetchPolicy: "cache-first"
  });

  const bands = data?.person?.bands ?? [];
  const suspensions = canViewPersonSuspensionHistory ? (suspensionsData?.person?.ccmsSuspensions ?? []) : [];
  const systemActiveDays = data?.person?.activeDaysOfMembership ?? null;
  const { bandRows, mergedBandDays, suspensionDays } = buildActiveYearsBreakdown(bands, suspensions);
  const totalDisplay =
    activeYearsValue ??
    (systemActiveDays != null
      ? formatYearsAndMonths(systemActiveDays)
      : formatYearsAndMonths(mergedBandDays - suspensionDays));

  const handleViewMembership = () => {
    onClose();
    const membershipOverviewPath = `/people/${pgaId}/membership/overview`;
    if (location.pathname !== membershipOverviewPath) {
      navigate("/people/:pgaId/membership/overview", { params: { pgaId } });
    }
  };

  return (
    <Dialog
      className="w-[800px] max-w-[95vw] max-h-[85vh]"
      open={open}
      onClosed={onClose}
      headline="Active Years of Membership"
      hasFormElements
      fixedContent={
        <ActiveYearsSummaryPanel
          totalDisplay={totalDisplay}
          mergedBandDays={mergedBandDays}
          suspensionDays={suspensionDays}
          systemActiveDays={systemActiveDays}
        />
      }
      fixedContentCompact
      actions={
        <>
          <Button variant="outlined" onClick={handleViewMembership} testId="active-years-view-membership">
            View Membership
          </Button>
          <Button variant="filled" onClick={onClose} testId="active-years-breakdown-close">
            Close
          </Button>
        </>
      }
    >
      <div data-testid="active-years-breakdown-modal">
        <ActiveYearsBandsTable bandRows={bandRows} loading={loading || suspensionsLoading} />
      </div>
    </Dialog>
  );
}
